import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { memo, useEffect, useReducer, useRef, } from 'react';
import LazyLoad from 'react-lazyload';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import windowMatchMedia from '../../utils/windowMatchMedia';
import { SwatchPicker, Swatch, } from '../SwatchPicker/index';
import { LAZY_LOAD_TOP_OFFSET, CYCLE_SPEED, FIRST_IMAGE_CYCLE_SPEED, } from './constants';
import { CustomColorSwatch } from './CustomColorSwatch';
import { FavoriteIconButton } from './FavoriteIconButton';
const touchDeviceBreakpoint = theme.breakpoints.medium;
const productCellStyles = css `
  box-sizing: border-box;
  padding: ${styleUtils.rem(16)} ${styleUtils.rem(19)};
  border: ${styleUtils.rem(1)} solid transparent;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr min-content;

  &:hover {
    border: ${styleUtils.rem(1)} solid ${theme.colors.gray400};
  }

  ${theme.media.lessThan(touchDeviceBreakpoint)(`
    padding: ${styleUtils.rem(10)};
    border: none;

    &:hover {
      border: none;
    }
  `)}
`;
const imageContainerStyles = (currentImage, imageHeight) => css `
  ${!currentImage && 'position: absolute;'}
  opacity: ${currentImage ? 1 : 0};
  display: grid;
  place-items: center;
  ${imageHeight ? `height: ${styleUtils.rem(imageHeight)};` : ''}
`;
/* Uncomment box-shadow property in imageStyles when all images are expeted
to have no white edges and shadow is desired */
const imageStyles = (withShadow) => css `
  display: block;
  width: 100%;

  /* box-shadow: ${withShadow && `0 ${styleUtils.rem(4)} ${styleUtils.rem(4)} rgba(0, 0, 0, 0.25)`}; */
`;
const descriptionContainerStyles = (showSwatchPicker) => css `
  display: grid;
  grid-template-columns: ${showSwatchPicker ? '6fr 4fr' : '6fr 1fr'};
  padding-top: ${styleUtils.rem(16)};

  ${theme.media.lessThan(touchDeviceBreakpoint)(`
    grid-template-columns: ${showSwatchPicker ? '1fr' : '4fr 1fr'};
    padding-top: ${styleUtils.rem(9)};
  `)}
`;
const textSectionStyles = (showSwatchPicker) => css `
  grid-column: 1 / 2;
  grid-row: 1 / 2;
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;

  ${theme.media.lessThan(touchDeviceBreakpoint)(`
    grid-column: 1 / 2;
    grid-row: ${showSwatchPicker ? '2 / 3' : '1 / 2'};
  `)}
`;
const symbolsSectionStyles = (showSwatchPicker) => css `
  grid-column: 2 / 3;
  grid-row: 1 / 2;
  display: flex;
  flex-direction: column;

  ${theme.media.lessThan(touchDeviceBreakpoint)(`
    grid-column: ${showSwatchPicker ? '1 / 2' : '2 / 3'};
    grid-row: 1 / 2;
    display: grid;
    grid-template-columns: ${showSwatchPicker ? '5fr 1fr' : '1fr'};
    padding-bottom: ${showSwatchPicker ? `${styleUtils.rem(6)}` : '0'};
  `)}
`;
const productNameStyles = css `
  display: block;
  ${theme.typeStyles.bodySmallStyles}
  ${theme.typeStyles.bodyBoldStyles}
  text-decoration: none;
  overflow-wrap: break-word;

  &:hover {
    opacity: 0.8;
  }

  ${theme.media.lessThan(touchDeviceBreakpoint)(`
    font-size: ${styleUtils.rem(12)};
  `)}
`;
const artistNameStyles = css `
  display: block;
  ${theme.typeStyles.bodySmallStyles}
  align-self: flex-start;
  overflow-wrap: break-word;
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }

  ${theme.media.lessThan(touchDeviceBreakpoint)(`
    font-size: ${styleUtils.rem(12)};
  `)}
`;
const merchandisingLineStyles = css `
  ${theme.typeStyles.bodySmallStyles}
  align-self: flex-start;
  margin: 0;

  ${theme.media.lessThan(touchDeviceBreakpoint)(`
    font-size: ${styleUtils.rem(12)};
  `)}
`;
const swatchPickerStyles = css `
  display: flex;
  justify-content: flex-end;
  margin-bottom: ${theme.spacing.base};
  flex-grow: 1;

  ${theme.media.lessThan(touchDeviceBreakpoint)(`
    grid-row: 1 / 2;

    justify-content: flex-start;

    margin-bottom: 0; /* stylelint-disable-line declaration-block-no-duplicate-properties */
  `)}
`;
const favoriteIconStyles = (showSwatchPicker) => css `
  margin-right: ${styleUtils.rem(10)};
  margin-top: ${styleUtils.rem(3)};
  display: flex;
  justify-content: flex-end;

  ${theme.media.lessThan(touchDeviceBreakpoint)(`
    grid-column: ${showSwatchPicker ? '2 / 3' : '1 / 2'};
    grid-row: 1 / 2;
    margin-right: 0;
    align-items: start;
    min-width: ${styleUtils.rem(20)};
  `)}
`;
const actionTypes = {
    cycleImage: 'cycleImage',
    resetCycling: 'resetCycling',
    setImageHeight: 'setImageHeight',
    setTouchDeviceScreen: 'setTouchDeviceScreen',
    startCycling: 'startCycling',
};
const initialState = {
    cyclingImage: false,
    displayedImage: 0,
    favoriteSuggestionOpen: undefined,
    firstCycle: true,
    imageHeight: undefined,
    touchDeviceScreen: false,
};
const reducerFactory = (productImages = [], cycleOnHover) => (state, action) => {
    const { touchDeviceScreen, } = state;
    const getProductImages = () => ((!touchDeviceScreen && cycleOnHover) ?
        productImages
        : [
            productImages[0],
        ]).filter(Boolean);
    switch (action.type) {
        case actionTypes.startCycling:
            return Object.assign(Object.assign({}, state), { cyclingImage: true });
        case actionTypes.resetCycling:
            return Object.assign(Object.assign({}, state), { cyclingImage: false, displayedImage: 0, firstCycle: true });
        case actionTypes.cycleImage: {
            const { displayedImage = 0, } = state;
            return Object.assign(Object.assign({}, state), { displayedImage: (displayedImage + 1) % getProductImages().length, firstCycle: false });
        }
        case actionTypes.setTouchDeviceScreen:
            return Object.assign(Object.assign({}, state), { touchDeviceScreen: action.value });
        case actionTypes.setImageHeight:
            return Object.assign(Object.assign({}, state), { imageHeight: action.height });
        default:
            throw new Error("Unexpected action dispatched to ProductCellQuaternary's reducer");
    }
};
const ProductCellQuaternary = ({ artistName, artistUrl, buyUrl, colorwayColors = [], favorited, lazyloadImage, merchandisingLine = undefined, onColorChange, onCustomColorClick = undefined, onCycle = () => { }, onFavoriteClick = () => { }, productImages, productName, selectedColor, showColorSwatch = true, }) => {
    // Disabling cycleOnHover until jumping cell size issue is resolved.
    const cycleOnHover = false;
    const imgRef = useRef(null);
    const getProductImagesToRender = (touchDeviceScreen) => {
        const images = (productImages
            && productImages.length > 0
            ? productImages
            : []);
        return (((!touchDeviceScreen
            && cycleOnHover)
            ? images
            : [
                images[0],
            ])
            .filter(Boolean));
    };
    const [state, dispatch,] = useReducer(reducerFactory(productImages, cycleOnHover), initialState, undefined);
    useEffect(() => {
        // complete will fire when image is cached and onLoad doesn't fire.
        if (imgRef.current && imgRef.current.complete) {
            dispatch({
                height: imgRef.current.offsetHeight,
                type: actionTypes.setImageHeight,
            });
        }
    }, [
        imgRef,
        productName,
    ]);
    const { cyclingImage, displayedImage, firstCycle, imageHeight, touchDeviceScreen, } = state;
    useEffect(() => {
        let cycleIntervalId;
        const mediaQueryList = window.matchMedia(`(max-width: ${touchDeviceBreakpoint})`);
        dispatch({
            type: actionTypes.setTouchDeviceScreen,
            value: mediaQueryList.matches,
        });
        const mediaQueryListChangeHandler = ({ matches, }) => dispatch({
            type: actionTypes.setTouchDeviceScreen,
            value: matches,
        });
        windowMatchMedia.addListener(mediaQueryList, mediaQueryListChangeHandler);
        clearInterval(cycleIntervalId);
        if (cyclingImage) {
            cycleIntervalId = setInterval(() => {
                dispatch({
                    type: actionTypes.cycleImage,
                });
                onCycle();
            }, 
            // first picture should have a shorter cycle speed to see a change
            firstCycle ? FIRST_IMAGE_CYCLE_SPEED : CYCLE_SPEED);
        }
        return () => {
            clearInterval(cycleIntervalId);
            if (mediaQueryList) {
                windowMatchMedia.removeListener(mediaQueryList, mediaQueryListChangeHandler);
            }
        };
    }, [
        onCycle,
        cyclingImage,
        firstCycle,
    ]);
    const handleFavoriteClick = () => {
        onFavoriteClick();
    };
    const productImagesToRender = getProductImagesToRender(touchDeviceScreen);
    const productImageNodes = (imageHeight = 1) => productImagesToRender.map((image, index) => (
    // IMPORTANT: For an unknown reason React treats scene7 URLs as the same key.
    // This is why we needed to add the `index` to the key. See FE-4808.
    // Please, do not remove the index from the key unless there's a better fix for that issue.
    _jsx("div", Object.assign({ css: [
            image.height && css `height: ${styleUtils.rem(image.height)};`,
            imageContainerStyles(displayedImage === index, imageHeight),
        ] }, { children: _jsx("img", { alt: image.alt, css: imageStyles(index === 0), onLoad: (event) => {
                // onLoad will fire when selecting filters and rerendering of pages
                dispatch({
                    height: event.target.offsetHeight,
                    type: actionTypes.setImageHeight,
                });
            }, ref: imgRef, src: image.url }) }), `${index}-${image.url}`)));
    //  SwatchPicker can't have undefined within its children,
    //  thus we need to filter the children array
    const swatchPickerChildren = [
        ...colorwayColors.map(({ colorValue, inputValue, name, }) => (_jsx(Swatch, { color: colorValue, displayName: name, id: `swatch-${inputValue}-${buyUrl}`, value: inputValue }, inputValue))),
        onCustomColorClick && (_jsx(CustomColorSwatch, { displayName: "Custom Color", onClick: onCustomColorClick }, "custom-color")),
    ].filter(Boolean);
    const showSwatchPicker = colorwayColors.length > 0 && showColorSwatch;
    // NOTE -- CYCLE ON HOVER DOES NOT LOOK GOOD IN MASONRY LAYOUT DUE TO VARYING ASPECT RATIOS OF THE IMAGES
    // MAY WANT TO DISABLE CYCLE ON HOVER FOR THE MASONRY LAYOUT
    return (_jsxs("div", Object.assign({ css: productCellStyles }, { children: [_jsx("a", Object.assign({ href: buyUrl, onMouseEnter: cycleOnHover ?
                    () => dispatch({
                        type: actionTypes.startCycling,
                    })
                    : undefined, onMouseLeave: cycleOnHover ?
                    () => dispatch({
                        type: actionTypes.resetCycling,
                    })
                    : undefined }, { children: lazyloadImage ?
                    (_jsx(LazyLoad, Object.assign({ offset: LAZY_LOAD_TOP_OFFSET, once: true }, { children: productImageNodes(imageHeight) })))
                    : productImageNodes(imageHeight) })), _jsxs("div", Object.assign({ css: descriptionContainerStyles(showSwatchPicker) }, { children: [_jsxs("div", Object.assign({ css: textSectionStyles(showSwatchPicker) }, { children: [_jsx("a", Object.assign({ css: productNameStyles, href: buyUrl }, { children: productName })), _jsx("a", Object.assign({ css: artistNameStyles, href: artistUrl }, { children: artistName })), merchandisingLine && (_jsx("p", Object.assign({ css: merchandisingLineStyles }, { children: merchandisingLine })))] })), _jsxs("div", Object.assign({ css: symbolsSectionStyles(showSwatchPicker) }, { children: [showSwatchPicker && (_jsx("div", Object.assign({ css: swatchPickerStyles }, { children: _jsx(SwatchPicker, Object.assign({ name: `color-picker-${buyUrl}`, onChange: onColorChange, selectionType: "single", size: touchDeviceScreen ? 'small' : 'xsmall', value: selectedColor }, { children: swatchPickerChildren })) }))), _jsx("div", Object.assign({ css: favoriteIconStyles(showSwatchPicker) }, { children: _jsx(FavoriteIconButton, { favorited: Boolean(favorited), onFavoriteClick: handleFavoriteClick, size: "extraSmall" }) }))] }))] }))] })));
};
const MemoizedProductCellQuaternary = memo(ProductCellQuaternary);
export { MemoizedProductCellQuaternary as ProductCellQuaternary };
// MC-1068 Temporary hack for Storybook.
export default ProductCellQuaternary;
